import { ReactElement } from 'react';
import { format } from 'date-fns';

import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { AmountCell } from 'modules/delegateStaking/components/AmountCell';
import { PoolCell } from 'modules/delegateStaking/components/PoolCell';
import { UnlockClaimCell } from 'modules/delegateStaking/components/UnlockClaimCell/UnlockClaimCell';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetPoolMetaQuery } from 'modules/pool/actions/getPoolMeta';
import { IPoolUnstake } from 'modules/pool/types';

import { StakingTable as Table } from '../StakingTable';
import { translation } from './translation';
import { useStyles } from './useStyles';

interface IDelegateTableProps {
  className?: string;
  poolUnstake: IPoolUnstake;
}

export function TableRow({
  className,
  poolUnstake,
}: IDelegateTableProps): ReactElement | null {
  const { classes } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);

  const { amount, executedAt, poolAddress } = poolUnstake;

  const { data: poolMeta } = useGetPoolMetaQuery({
    address: poolAddress,
  });

  const usdAmount = useGetUSDAmount(amount);

  return (
    <Table.Row className={className}>
      <PoolCell
        address={poolUnstake.poolAddress}
        className={classes.poolCell}
        poolLogoIpfsLink={poolMeta?.ipfsImageLink}
        poolName={poolMeta?.name}
      />

      <AmountCell
        amount={t(keys.unit.tokenValue, {
          value: amount.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
          token: t(keys.tokens.main),
        })}
        className={classes.commonMdCell}
        mdLabel={t(keys.amount)}
        subAmount={
          usdAmount && !usdAmount.isZero() && amount
            ? t(keys.unit.$, {
                value: usdAmount.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
                token: t(keys.tokens.main),
              })
            : undefined
        }
      />

      <AmountCell
        amount={format(executedAt, 'MMM dd, yyyy')}
        className={classes.commonMdCell}
        mdLabel={t(keys.initiated)}
        subAmount={format(executedAt, 'hh:mm a')}
      />

      <UnlockClaimCell
        align="right"
        className={classes.actions}
        executedAt={executedAt}
        poolAddress={poolAddress}
      />
    </Table.Row>
  );
}
